import React, { useEffect, useState } from 'react';

import { submitDataFlag } from '../services/flagsApi';

/**
 * "Flag bad data" modal.
 *
 * Surfaced from the parcel detail page (top-right of the header
 * action bar). Lets a user pick a category, leave an optional
 * comment + email, and POST the result to /parcels/{id}/flags.
 *
 * Styling matches ReportViewer.js (the other modal on the parcel
 * detail page) — fixed-position dimmed-backdrop overlay, white
 * panel, click-outside-to-close. We deliberately don't pull in a
 * modal library: ReportViewer hand-rolls this same pattern and a
 * second one-off modal isn't worth a dependency.
 *
 * Success path: shows an inline confirmation message (the parent
 * page's flag count refreshes via onFlagSubmitted callback) and
 * stays open for ~1.5s so the user sees feedback before we unmount.
 */

// Display labels for the backend's category Literal values. Kept
// here rather than imported from the API client because this is
// the only place that needs the user-facing strings — the API
// client only knows the raw keys.
const CATEGORY_OPTIONS = [
  { value: 'acreage', label: 'Acreage is wrong' },
  { value: 'address', label: 'Address is wrong' },
  { value: 'owner', label: 'Owner is wrong' },
  { value: 'geometry', label: 'Parcel boundary is wrong' },
  { value: 'price', label: 'Price is wrong' },
  { value: 'mls_status', label: 'MLS status is wrong (off-market, pending, etc.)' },
  { value: 'mineral_rights', label: 'Mineral rights are wrong' },
  { value: 'hazard', label: 'Hazard score is wrong' },
  { value: 'other', label: 'Other' },
];

// Hard cap matches backend Pydantic max_length=4000. We render a
// remaining-chars counter against this so the user sees the limit
// before the server rejects them.
const COMMENT_MAX = 4000;

export default function FlagDataModal({ parcelId, onClose, onFlagSubmitted }) {
  const [category, setCategory] = useState('acreage');
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);

  // Escape closes the modal — same affordance as ReportViewer so
  // the two modals feel consistent. We don't intercept other keys.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !submitting) onClose && onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  // After a successful submit we keep the modal open briefly so
  // the user sees the confirmation, then auto-close. The parent
  // page's flag count refresh fires immediately via
  // onFlagSubmitted so the indicator is already updated by the
  // time the modal unmounts.
  useEffect(() => {
    if (!submittedAt) return;
    const t = setTimeout(() => onClose && onClose(), 1500);
    return () => clearTimeout(t);
  }, [submittedAt, onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const row = await submitDataFlag(parcelId, {
        category,
        comment,
        reporter_email: email,
      });
      setSubmittedAt(Date.now());
      onFlagSubmitted && onFlagSubmitted(row);
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to submit flag');
    } finally {
      setSubmitting(false);
    }
  }

  const remaining = COMMENT_MAX - comment.length;
  const isSuccess = submittedAt !== null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Flag bad parcel data"
      onClick={() => !submitting && onClose && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(2px)',
        zIndex: 4000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2vh 2vw',
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          width: 'min(520px, 96vw)',
          borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
            Flag bad data
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose && onClose()}
            aria-label="Close flag-data modal"
            className="btn btn-outline"
            disabled={submitting}
            style={{ padding: '4px 10px', fontSize: '0.78rem' }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isSuccess ? (
            <div
              role="status"
              style={{
                padding: '10px 12px',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                color: '#065f46',
                borderRadius: 6,
                fontSize: '0.85rem',
              }}
            >
              Thanks — your flag was recorded. Ops will review it shortly.
            </div>
          ) : (
            <>
              <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                Tell us what looks wrong with this parcel. Reports go into our
                ops review queue.
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                  What is wrong?
                </span>
                <select
                  aria-label="Flag category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={submitting}
                  style={{
                    padding: '6px 8px',
                    fontSize: '0.88rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: 4,
                    background: '#fff',
                  }}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                  Details (optional)
                </span>
                <textarea
                  aria-label="Flag comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
                  disabled={submitting}
                  rows={4}
                  placeholder="What did you see? e.g. 'Listed 23000 acres but the polygon is only ~150 acres'"
                  style={{
                    padding: '6px 8px',
                    fontSize: '0.88rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: 4,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <span style={{ fontSize: '0.7rem', color: '#94a3b8', alignSelf: 'flex-end' }}>
                  {remaining} chars left
                </span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                  Your email (optional — leave blank for anonymous)
                </span>
                <input
                  type="email"
                  aria-label="Reporter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  placeholder="you@example.com"
                  style={{
                    padding: '6px 8px',
                    fontSize: '0.88rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: 4,
                  }}
                />
              </label>

              {error && (
                <div
                  role="alert"
                  style={{
                    padding: '8px 10px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#991b1b',
                    borderRadius: 6,
                    fontSize: '0.82rem',
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => !submitting && onClose && onClose()}
                  disabled={submitting}
                  style={{ padding: '6px 14px', fontSize: '0.82rem' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                  style={{ padding: '6px 14px', fontSize: '0.82rem' }}
                >
                  {submitting ? 'Submitting…' : 'Submit flag'}
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
